import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import { DataTable } from '../../shared';
import DashboardReportHeader from '../common/DashboardReportHeader';
import { getStockCategories, getStockReport } from '../../services/api';
import { downloadPdfTable } from '../../utils/reportExport';
import { exportStockTable, printStockTable, STOCK_STATUS_COLORS, STOCK_STATUS_LABELS } from '../../utils/stockExportUtils';
import { getStockListPaperSx } from '../../utils/stockMobileLayout';
import StockItemLedgerDrawer from './StockItemLedgerDrawer';
import StockReportTypeMenu from './StockReportTypeMenu';

const REPORTS = [
  { id: 'stock_summary', label: 'Stock Summary', dated: true },
  { id: 'current_stock', label: 'Current Stock Report', dated: false },
  { id: 'stock_movement', label: 'Stock Movement Report', dated: true },
  { id: 'consumption', label: 'Consumption Report', dated: true },
  { id: 'adjustment', label: 'Adjustment Report', dated: true },
  { id: 'transfer', label: 'Transfer Report', dated: true },
  { id: 'low_stock', label: 'Low Stock Report', dated: false },
  { id: 'valuation', label: 'Inventory Valuation Report', dated: false },
];

const DEPARTMENT_COLUMN = { field: 'department_label', label: 'Department', sortable: true };

const REPORT_COLUMNS = {
  stock_summary: [
    { field: 'item_name', label: 'Item Name', sortable: true },
    DEPARTMENT_COLUMN,
    { field: 'category_label', label: 'Category', sortable: true },
    { field: 'opening_stock', label: 'Opening Stock', sortable: true },
    { field: 'stock_in', label: 'Stock In', sortable: true },
    { field: 'stock_out', label: 'Stock Out', sortable: true },
    { field: 'adjustments', label: 'Adjustments', sortable: true },
    { field: 'closing_stock', label: 'Closing Stock', sortable: true },
    { field: 'unit', label: 'Unit', sortable: true },
    { field: 'stock_value', label: 'Stock Value', sortable: true },
  ],
  current_stock: [
    { field: 'item_name', label: 'Item', sortable: true },
    DEPARTMENT_COLUMN,
    { field: 'category_label', label: 'Category', sortable: true },
    { field: 'current_quantity', label: 'Current Qty', sortable: true },
    { field: 'unit', label: 'Unit', sortable: true },
    { field: 'average_cost', label: 'Avg Cost', sortable: true },
    { field: 'inventory_value', label: 'Stock Value', sortable: true },
    { field: 'reorder_level', label: 'Reorder Level', sortable: true },
    {
      field: 'status_label',
      label: 'Status',
      sortable: true,
      render: (value, row) => (
        <Chip
          size="small"
          label={value}
          sx={{
            bgcolor: `${STOCK_STATUS_COLORS[row.status] || '#64748b'}22`,
            color: STOCK_STATUS_COLORS[row.status] || '#64748b',
            fontWeight: 600,
          }}
        />
      ),
    },
  ],
  stock_movement: [
    { field: 'date', label: 'Date', sortable: true },
    DEPARTMENT_COLUMN,
    { field: 'transaction_type_label', label: 'Type', sortable: true },
    { field: 'reference_number', label: 'Reference', sortable: true },
    { field: 'item_name', label: 'Item', sortable: true },
    { field: 'quantity_in', label: 'Qty In', sortable: true },
    { field: 'quantity_out', label: 'Qty Out', sortable: true },
    { field: 'balance_after', label: 'Balance', sortable: true },
    { field: 'user_name', label: 'User', sortable: true },
  ],
  consumption: [
    { field: 'item_name', label: 'Item', sortable: true },
    DEPARTMENT_COLUMN,
    { field: 'category_label', label: 'Category', sortable: true },
    { field: 'total_consumed', label: 'Total Consumed', sortable: true },
    { field: 'consumption_value', label: 'Value', sortable: true },
    { field: 'avg_daily_consumption', label: 'Avg Daily', sortable: true },
    { field: 'unit', label: 'Unit', sortable: true },
  ],
  adjustment: [
    { field: 'date', label: 'Date', sortable: true },
    { field: 'item_name', label: 'Item', sortable: true },
    DEPARTMENT_COLUMN,
    { field: 'previous_qty', label: 'Previous Qty', sortable: true },
    { field: 'adjusted_qty', label: 'Adjusted Qty', sortable: true },
    { field: 'difference', label: 'Difference', sortable: true },
    {
      field: 'adjustment_type',
      label: 'Type',
      sortable: true,
      render: (value) => (
        <Chip
          size="small"
          label={value === 'positive' ? 'Positive' : value === 'negative' ? 'Negative' : '—'}
          sx={{
            bgcolor: value === 'positive' ? '#66d4a822' : '#ef535022',
            color: value === 'positive' ? '#66d4a8' : '#ef5350',
            fontWeight: 600,
          }}
        />
      ),
    },
    { field: 'reason', label: 'Reason', sortable: true },
    { field: 'user_name', label: 'User', sortable: true },
  ],
  transfer: [
    { field: 'date', label: 'Date', sortable: true },
    DEPARTMENT_COLUMN,
    { field: 'from_location', label: 'From', sortable: true },
    { field: 'to_location', label: 'To', sortable: true },
    { field: 'item_name', label: 'Item', sortable: true },
    { field: 'quantity', label: 'Qty', sortable: true },
    { field: 'status', label: 'Status', sortable: true },
    { field: 'created_by', label: 'Created By', sortable: true },
  ],
  low_stock: [
    { field: 'item_name', label: 'Item', sortable: true },
    DEPARTMENT_COLUMN,
    { field: 'current_stock', label: 'Current Stock', sortable: true },
    { field: 'reorder_level', label: 'Reorder Level', sortable: true },
    { field: 'difference', label: 'Difference', sortable: true },
    { field: 'category_label', label: 'Category', sortable: true },
    { field: 'supplier_name', label: 'Supplier', sortable: true },
    {
      field: 'highlight',
      label: 'Alert',
      sortable: true,
      render: (value) => (
        <Chip
          size="small"
          label={value}
          sx={{
            bgcolor: value === 'Out Of Stock' ? '#ef535022' : value === 'Critical Stock' ? '#ff980022' : '#ffc10722',
            color: value === 'Out Of Stock' ? '#ef5350' : value === 'Critical Stock' ? '#ff9800' : '#ffc107',
            fontWeight: 600,
          }}
        />
      ),
    },
  ],
  valuation: [
    { field: 'item_name', label: 'Item', sortable: true },
    DEPARTMENT_COLUMN,
    { field: 'category_label', label: 'Category', sortable: true },
    { field: 'current_stock', label: 'Current Stock', sortable: true },
    { field: 'average_cost', label: 'Avg Cost', sortable: true },
    { field: 'total_value', label: 'Total Value', sortable: true },
    { field: 'unit', label: 'Unit', sortable: true },
  ],
};

const toExportColumns = (columns) =>
  columns.map((col) => ({ key: col.field, label: col.label }));

const DRILLDOWN_REPORTS = new Set([
  'stock_summary',
  'current_stock',
  'consumption',
  'adjustment',
  'low_stock',
  'valuation',
]);

const REPORT_PAGE_SIZE = 10;
const SEARCH_DEBOUNCE_MS = 350;

const INITIAL_COLUMN_FILTERS = {
  item: '',
  department: '',
  category: '',
  status: '',
  transactionType: '',
  reference: '',
  user: '',
  adjustmentType: '',
  highlight: '',
  supplier: '',
  fromLocation: '',
  toLocation: '',
};

const INITIAL_DEBOUNCED_TEXT_FILTERS = {
  item: '',
  reference: '',
  user: '',
  supplier: '',
  fromLocation: '',
  toLocation: '',
};

const DEPARTMENT_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'hotel', label: 'Hotel' },
  { value: 'restaurant', label: 'Restaurant' },
];

const STATUS_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'in_stock', label: 'Normal' },
  { value: 'low_stock', label: 'Low Stock' },
  { value: 'out_of_stock', label: 'Out Of Stock' },
];

const TRANSACTION_TYPE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'OPENING_STOCK', label: 'Opening Stock' },
  { value: 'STOCK_IN', label: 'Stock Addition' },
  { value: 'STOCK_OUT', label: 'Consumption' },
  { value: 'ADJUSTMENT', label: 'Adjustment' },
  { value: 'TRANSFER_IN', label: 'Transfer In' },
  { value: 'TRANSFER_OUT', label: 'Transfer Out' },
];

const ADJUSTMENT_TYPE_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'positive', label: 'Positive' },
  { value: 'negative', label: 'Negative' },
];

const HIGHLIGHT_FILTER_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'Out Of Stock', label: 'Out Of Stock' },
  { value: 'Critical Stock', label: 'Critical Stock' },
  { value: 'Reorder Required', label: 'Reorder Required' },
];

const filterFieldSx = { width: '100%' };

const FilterSearchField = ({ fieldKey, placeholder, value, onChange }) => (
  <TextField
    key={fieldKey}
    placeholder={placeholder}
    size="small"
    value={value}
    onChange={onChange}
    variant="standard"
    InputProps={{
      disableUnderline: true,
      startAdornment: (
        <InputAdornment position="start">
          <SearchIcon fontSize="small" />
        </InputAdornment>
      ),
    }}
    sx={filterFieldSx}
  />
);

const FilterSelectField = ({ fieldKey, value, onChange, options }) => (
  <FormControl key={fieldKey} size="small" sx={filterFieldSx}>
    <Select value={value} onChange={onChange} displayEmpty variant="standard" disableUnderline>
      {options.map((opt) => (
        <MenuItem key={opt.value || '__all__'} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

const StockReportsSection = ({ isDark }) => {
  const [report, setReport] = useState('current_stock');
  const [start, setStart] = useState(() => moment().startOf('month'));
  const [end, setEnd] = useState(() => moment().endOf('month'));
  const [columnFilters, setColumnFilters] = useState(INITIAL_COLUMN_FILTERS);
  const [debouncedTextFilters, setDebouncedTextFilters] = useState(INITIAL_DEBOUNCED_TEXT_FILTERS);
  const [categories, setCategories] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [meta, setMeta] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [pagination, setPagination] = useState({ page: 0, perPage: REPORT_PAGE_SIZE, totalRecords: 0 });
  const fetchSeqRef = useRef(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });
  const [ledgerDrawer, setLedgerDrawer] = useState({
    open: false,
    itemId: null,
    itemName: '',
    department: '',
    departmentLabel: '',
  });

  const reportConfig = REPORTS.find((r) => r.id === report) || REPORTS[0];
  const columns = useMemo(() => {
    const base = REPORT_COLUMNS[report] || REPORT_COLUMNS.current_stock;
    if (!DRILLDOWN_REPORTS.has(report)) {
      return base;
    }
    return [
      ...base,
      {
        field: 'history',
        label: 'History',
        sortable: false,
        render: (_, row) => (
          <Button
            size="small"
            variant="outlined"
            startIcon={<ReceiptLongIcon sx={{ fontSize: 16 }} />}
            onClick={() =>
              setLedgerDrawer({
                open: true,
                itemId: row.item_id,
                itemName: row.item_name,
                department: row.department || '',
                departmentLabel: row.department_label || '',
              })
            }
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            View
          </Button>
        ),
      },
    ];
  }, [report]);

  const exportColumns = useMemo(() => toExportColumns(REPORT_COLUMNS[report] || []), [report]);

  const categoryFilterOptions = useMemo(
    () => [{ value: '', label: 'All' }, ...categories.map((cat) => ({ value: cat.value, label: cat.label }))],
    [categories],
  );

  const hasActiveColumnFilters = useMemo(
    () =>
      Object.values(columnFilters).some((value) => String(value || '').trim() !== '') ||
      Object.values(debouncedTextFilters).some((value) => String(value || '').trim() !== ''),
    [columnFilters, debouncedTextFilters],
  );

  const updateColumnFilter = useCallback((field, value) => {
    setColumnFilters((prev) => ({ ...prev, [field]: value }));
    setPagination((prev) => ({ ...prev, page: 0 }));
  }, []);

  const clearColumnFilters = useCallback(() => {
    setColumnFilters(INITIAL_COLUMN_FILTERS);
    setDebouncedTextFilters(INITIAL_DEBOUNCED_TEXT_FILTERS);
    setPagination((prev) => ({ ...prev, page: 0 }));
  }, []);

  useEffect(() => {
    getStockCategories()
      .then((res) => setCategories(Array.isArray(res?.categories) ? res.categories : []))
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedTextFilters({
        item: columnFilters.item.trim(),
        reference: columnFilters.reference.trim(),
        user: columnFilters.user.trim(),
        supplier: columnFilters.supplier.trim(),
        fromLocation: columnFilters.fromLocation.trim(),
        toLocation: columnFilters.toLocation.trim(),
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [
    columnFilters.item,
    columnFilters.reference,
    columnFilters.user,
    columnFilters.supplier,
    columnFilters.fromLocation,
    columnFilters.toLocation,
  ]);

  const runReport = useCallback(async () => {
    const fetchId = fetchSeqRef.current + 1;
    fetchSeqRef.current = fetchId;
    setLoading(true);
    try {
      const params = {
        report,
        page: pagination.page + 1,
        page_size: pagination.perPage,
        search: debouncedTextFilters.item || undefined,
        category: columnFilters.category || undefined,
        department: columnFilters.department || undefined,
        user: debouncedTextFilters.user || undefined,
        reference: debouncedTextFilters.reference || undefined,
        transaction_type: columnFilters.transactionType || undefined,
        status: columnFilters.status || undefined,
        adjustment_type: columnFilters.adjustmentType || undefined,
        highlight: columnFilters.highlight || undefined,
        supplier: debouncedTextFilters.supplier || undefined,
        from_location: debouncedTextFilters.fromLocation || undefined,
        to_location: debouncedTextFilters.toLocation || undefined,
      };
      if (reportConfig.dated) {
        params.start_date = start.format('YYYY-MM-DD');
        params.end_date = end.format('YYYY-MM-DD');
      }
      const res = await getStockReport(params);
      if (fetchId !== fetchSeqRef.current) return;
      const nextRows = (res.rows || []).map((row) => ({
        ...row,
        status_label: row.status_label || STOCK_STATUS_LABELS[row.status] || row.status,
      }));
      setRows(nextRows);
      setMeta({
        total_value: res.total_value,
        category_breakdown: res.category_breakdown,
        top_valuable_items: res.top_valuable_items,
      });
      setAnalytics(res.analytics || null);
      setPagination((prev) => ({
        ...prev,
        totalRecords: res.pagination?.total_records ?? nextRows.length,
      }));
    } catch {
      if (fetchId !== fetchSeqRef.current) return;
      setSnackbar({ open: true, message: 'Failed to generate report.', severity: 'error' });
    } finally {
      if (fetchId === fetchSeqRef.current) {
        setLoading(false);
      }
    }
  }, [
    report,
    reportConfig.dated,
    start,
    end,
    debouncedTextFilters,
    columnFilters.category,
    columnFilters.department,
    columnFilters.transactionType,
    columnFilters.status,
    columnFilters.adjustmentType,
    columnFilters.highlight,
    pagination.page,
    pagination.perPage,
  ]);

  const filterRowCells = useMemo(
    () =>
      columns.map((column) => {
        switch (column.field) {
          case 'item_name':
            return (
              <FilterSearchField
                fieldKey={column.field}
                placeholder="Search item..."
                value={columnFilters.item}
                onChange={(e) => updateColumnFilter('item', e.target.value)}
              />
            );
          case 'department_label':
            return (
              <FilterSelectField
                fieldKey={column.field}
                value={columnFilters.department}
                onChange={(e) => updateColumnFilter('department', e.target.value)}
                options={DEPARTMENT_FILTER_OPTIONS}
              />
            );
          case 'category_label':
            return (
              <FilterSelectField
                fieldKey={column.field}
                value={columnFilters.category}
                onChange={(e) => updateColumnFilter('category', e.target.value)}
                options={categoryFilterOptions}
              />
            );
          case 'status_label':
            return (
              <FilterSelectField
                fieldKey={column.field}
                value={columnFilters.status}
                onChange={(e) => updateColumnFilter('status', e.target.value)}
                options={STATUS_FILTER_OPTIONS}
              />
            );
          case 'transaction_type_label':
            return (
              <FilterSelectField
                fieldKey={column.field}
                value={columnFilters.transactionType}
                onChange={(e) => updateColumnFilter('transactionType', e.target.value)}
                options={TRANSACTION_TYPE_OPTIONS}
              />
            );
          case 'reference_number':
            return (
              <FilterSearchField
                fieldKey={column.field}
                placeholder="Search reference..."
                value={columnFilters.reference}
                onChange={(e) => updateColumnFilter('reference', e.target.value)}
              />
            );
          case 'user_name':
          case 'created_by':
            return (
              <Box key={column.field} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
                <FilterSearchField
                  fieldKey={`${column.field}-search`}
                  placeholder="Search user..."
                  value={columnFilters.user}
                  onChange={(e) => updateColumnFilter('user', e.target.value)}
                />
                {hasActiveColumnFilters && !DRILLDOWN_REPORTS.has(report) && (
                  <Button size="small" variant="outlined" onClick={clearColumnFilters} sx={{ alignSelf: 'flex-start' }}>
                    Clear
                  </Button>
                )}
              </Box>
            );
          case 'adjustment_type':
            return (
              <FilterSelectField
                fieldKey={column.field}
                value={columnFilters.adjustmentType}
                onChange={(e) => updateColumnFilter('adjustmentType', e.target.value)}
                options={ADJUSTMENT_TYPE_OPTIONS}
              />
            );
          case 'from_location':
            return (
              <FilterSearchField
                fieldKey={column.field}
                placeholder="Search from..."
                value={columnFilters.fromLocation}
                onChange={(e) => updateColumnFilter('fromLocation', e.target.value)}
              />
            );
          case 'to_location':
            return (
              <FilterSearchField
                fieldKey={column.field}
                placeholder="Search to..."
                value={columnFilters.toLocation}
                onChange={(e) => updateColumnFilter('toLocation', e.target.value)}
              />
            );
          case 'supplier_name':
            return (
              <FilterSearchField
                fieldKey={column.field}
                placeholder="Search supplier..."
                value={columnFilters.supplier}
                onChange={(e) => updateColumnFilter('supplier', e.target.value)}
              />
            );
          case 'highlight':
            return (
              <FilterSelectField
                fieldKey={column.field}
                value={columnFilters.highlight}
                onChange={(e) => updateColumnFilter('highlight', e.target.value)}
                options={HIGHLIGHT_FILTER_OPTIONS}
              />
            );
          case 'history':
            return hasActiveColumnFilters ? (
              <Box key={column.field} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button size="small" variant="outlined" onClick={clearColumnFilters}>
                  Clear
                </Button>
              </Box>
            ) : null;
          default:
            return null;
        }
      }),
    [
      columns,
      columnFilters,
      categoryFilterOptions,
      hasActiveColumnFilters,
      updateColumnFilter,
      clearColumnFilters,
      report,
    ],
  );

  useEffect(() => {
    runReport();
  }, [runReport]);

  const handleReportChange = (nextReport) => {
    setReport(nextReport);
    setColumnFilters(INITIAL_COLUMN_FILTERS);
    setDebouncedTextFilters(INITIAL_DEBOUNCED_TEXT_FILTERS);
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handleDateRangeChange = useCallback((nextStart, nextEnd) => {
    if (nextStart) setStart(nextStart);
    if (nextEnd) setEnd(nextEnd);
    setPagination((prev) => ({ ...prev, page: 0 }));
  }, []);

  const handlePdfExport = useCallback(() => {
    downloadPdfTable(reportConfig.label, '', exportColumns, rows, report);
  }, [exportColumns, report, reportConfig.label, rows]);

  const handleExcelExport = useCallback(() => {
    exportStockTable(exportColumns, rows, report, 'excel');
  }, [exportColumns, report, rows]);

  const handleCsvExport = useCallback(() => {
    exportStockTable(exportColumns, rows, report, 'csv');
  }, [exportColumns, report, rows]);

  const handlePrintExport = useCallback(() => {
    printStockTable(reportConfig.label, exportColumns, rows);
  }, [exportColumns, reportConfig.label, rows]);

  const dateRangeSubtitle = `${start.format('DD MMM YYYY')} – ${end.format('DD MMM YYYY')}`;

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box sx={{ mt: 2 }}>
        <Paper elevation={0} sx={getStockListPaperSx(isDark)}>
          <DashboardReportHeader
            title="Inventory Reports"
            subtitle={dateRangeSubtitle}
            startDate={start}
            endDate={end}
            onDateRangeChange={handleDateRangeChange}
            onRefresh={runReport}
            onPdfDownload={handlePdfExport}
            onExcelDownload={handleExcelExport}
            onCsvDownload={handleCsvExport}
            onPrintDownload={handlePrintExport}
            loading={loading}
            pdfDisabled={!rows.length}
            themeMode={isDark ? 'dark' : 'light'}
            initialPreset="this_month"
            showDateFilter
            showExcel
            showCsv
            showPrint
          />

          {meta.total_value && (
            <Typography sx={{ mb: 1, fontWeight: 600 }}>
              Total inventory value: ₹{Number(meta.total_value).toLocaleString('en-IN')}
            </Typography>
          )}

          {analytics?.top_consumed_items?.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                Top consumed items
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {analytics.top_consumed_items.slice(0, 5).map((item) => (
                  <Chip
                    key={item.item_id || item.item_name}
                    size="small"
                    label={`${item.item_name}: ${item.total_consumed} ${item.unit || ''}`.trim()}
                  />
                ))}
              </Box>
            </Box>
          )}

          {meta.category_breakdown?.length > 0 && (
            <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {meta.category_breakdown.map((row) => (
                <Chip
                  key={row.category}
                  size="small"
                  variant="outlined"
                  label={`${row.category}: ₹${Number(row.total_value).toLocaleString('en-IN')}`}
                />
              ))}
            </Box>
          )}

          <DataTable
            columns={columns}
            data={rows}
            loading={loading}
            skeletonRowCount={pagination.perPage}
            pagination={pagination}
            onPageChange={(page) => setPagination((prev) => ({ ...prev, page }))}
            onPerPageChange={(perPage) =>
              setPagination((prev) => ({ ...prev, perPage, page: 0 }))
            }
            filterRow={filterRowCells}
            toolbarRight={
              <StockReportTypeMenu
                isDark={isDark}
                reports={REPORTS}
                value={report}
                onChange={handleReportChange}
              />
            }
            stickyHeader
            optimizeScroll
            emptyMessage={loading ? 'Loading report…' : 'No records found for this report.'}
          />
        </Paper>

        <StockItemLedgerDrawer
          open={ledgerDrawer.open}
          onClose={() =>
            setLedgerDrawer({
              open: false,
              itemId: null,
              itemName: '',
              department: '',
              departmentLabel: '',
            })
          }
          itemId={ledgerDrawer.itemId}
          itemName={ledgerDrawer.itemName}
          purchaseFor={ledgerDrawer.department || undefined}
          purchaseForLabel={ledgerDrawer.departmentLabel || undefined}
          isDark={isDark}
        />

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default StockReportsSection;
